import { useEffect, useRef, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useLazyQuery } from "@apollo/client";

import { StackCreationVersionType, StackVendorFormFields } from "../../types";
import { CHECK_OPEN_TOFU_VERSION } from "../Terraform/gql";

const DEBOUNCE_TIMEOUT = 300;

const useAsyncRangeVersionValidation = (
  setAsyncValidationLoading: (isLoading: boolean) => void
) => {
  const { setError, getValues, formState, clearErrors } = useFormContext<StackVendorFormFields>();
  const timeout = useRef<NodeJS.Timeout | undefined>(undefined);

  const stopLoading = useCallback(() => {
    if (!timeout.current) {
      setAsyncValidationLoading(false);
    }
  }, [setAsyncValidationLoading, timeout]);

  const onCompleted = () => {
    stopLoading();

    if (formState.errors.opentofu?.version?.type === "custom") {
      clearErrors("opentofu.version");
    }
  };

  const [checkOpenTofuVersion, { error }] = useLazyQuery(CHECK_OPEN_TOFU_VERSION);

  useEffect(() => {
    if (error) {
      stopLoading();

      if (!formState.errors.opentofu?.version) {
        setError("opentofu.version", {
          type: "custom",
          message: error?.message || "Invalid version range",
        });
      }
    }
  }, [error, formState.errors, setError, stopLoading]);

  const values = getValues();
  const openTofuValues = values.opentofu;

  useEffect(() => {
    if (
      openTofuValues.workflowTool &&
      openTofuValues.version.value &&
      openTofuValues.version.type === StackCreationVersionType.Range
    ) {
      setAsyncValidationLoading(true);
      timeout.current = setTimeout(() => {
        timeout.current = undefined;
        checkOpenTofuVersion({ variables: { constraints: openTofuValues.version.value } }).then(
          onCompleted
        );
      }, DEBOUNCE_TIMEOUT);
    }

    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = undefined;
      }

      setAsyncValidationLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openTofuValues.version, openTofuValues.workflowTool]);
};

export default useAsyncRangeVersionValidation;
